<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'
import clubApplicationService from '../services/clubApplication.service'
import type { MyClubApplication } from '../types/clubApplication.types'

const authStore = useAuthStore()
const { t } = useI18n()

// Get user email
const userEmail = computed(() => authStore.user?.email || '')

// Own club applications (signed up via club signup link, waiting for the club)
const myApplications = ref<MyClubApplication[]>([])

const fetchMyApplications = async () => {
  try {
    myApplications.value = await clubApplicationService.getMyApplications()
  } catch (err) {
    console.error('Fetch my club applications error:', err)
  }
}

onMounted(() => {
  fetchMyApplications()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen flex items-center justify-center px-4 py-8">
    <div class="w-full max-w-6xl">
      <!-- Page Header -->
      <div class="text-center mb-8">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('chooseProfile.title') }}</h1>
        <p class="text-sm text-gray-600 mt-2">{{ t('chooseProfile.subtitle') }}</p>
      </div>

      <!-- Own club applications -->
      <div v-if="myApplications.length > 0" class="bg-white border-2 border-gray-300 rounded shadow-md p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-800 mb-1">{{ t('chooseProfile.applicationsTitle') }}</h2>
        <p class="text-sm text-gray-600 mb-4">{{ t('chooseProfile.applicationsDescription') }}</p>
        <div class="space-y-2">
          <div
            v-for="application in myApplications"
            :key="application.uuid"
            class="flex items-center gap-3 p-3 border-2 border-gray-200 rounded"
          >
            <img
              v-if="application.club?.logo_url"
              :src="application.club.logo_url"
              :alt="application.club.name"
              class="w-10 h-10 object-contain flex-shrink-0"
            >
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900">{{ application.club?.name }}</p>
              <p class="text-xs text-gray-600">{{ application.first_name }} {{ application.last_name }}</p>
            </div>
            <span
              v-if="application.status === 'pending'"
              class="inline-block px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded flex-shrink-0"
            >{{ t('chooseProfile.applicationPending') }}</span>
            <span
              v-else-if="application.status === 'rejected'"
              class="inline-block px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded flex-shrink-0"
            >{{ t('chooseProfile.applicationRejected') }}</span>
          </div>
        </div>
      </div>

      <!-- Two Column Layout -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Left Side: Player/Parent -->
        <div class="bg-white border-2 border-gray-300 rounded shadow-md p-6">
          <h2 class="text-xl font-semibold text-gray-800 mb-3">{{ t('chooseProfile.playerTitle') }}</h2>
          <p class="text-sm text-gray-600 mb-4">
            {{ t('chooseProfile.playerDescription') }}
          </p>

          <!-- Display user email -->
          <div class="mt-6 p-3 bg-gray-100 border border-gray-300 rounded">
            <p class="text-xs text-gray-600 mb-1">{{ t('chooseProfile.yourEmail') }}</p>
            <p class="text-sm font-medium text-gray-900">{{ userEmail }}</p>
            <p class="text-xs text-gray-500 mt-2">{{ t('chooseProfile.shareEmail') }}</p>
          </div>
        </div>

        <!-- Right Side: Club/Organization -->
        <div class="bg-white border-2 border-gray-300 rounded shadow-md p-6">
          <h2 class="text-xl font-semibold text-gray-800 mb-3">{{ t('chooseProfile.clubTitle') }}</h2>
          <p class="text-sm text-gray-600 mb-6">
            {{ t('chooseProfile.clubDescription') }}
          </p>

          <!-- Register Club Button -->
          <router-link
            to="/register-club"
            class="block w-full py-3 px-4 bg-blue-600 text-white text-center font-medium rounded hover:bg-blue-700 transition"
          >
            {{ t('chooseProfile.registerClub') }}
          </router-link>

          <!-- Additional Info -->
          <div class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p class="text-xs text-blue-800">
              <strong>{{ t('chooseProfile.benefits') }}</strong> {{ t('chooseProfile.benefitsDescription') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
